import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Globe, Award } from 'lucide-react';
import { MktButton, Eyebrow } from './marketplaceUi';
import { USE_CASE_META, USE_CASE_ORDER } from './useCaseMeta';
import TwinExplorer from './TwinExplorer';
import { getSubCategories, getUseCases, type SubCategory } from './marketplaceApi';
import { PARTICIPANT_LABEL } from './marketplaceLinks';

const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

const FAQ_GROUPS: { group: string; items: { q: string; a: string }[] }[] = [
  {
    group: 'About the study',
    items: [
      { q: 'How were the participants recruited?', a: 'All participants were recruited through User Interviews, a professional research recruitment platform. Each completed a detailed screener to validate their engagement with the breakfast category. Quotas were set across age, gender, household income, geography, and breakfast category usage to ensure the panel is representative of real breakfast consumers in the US.' },
      { q: 'Are these real people or fully synthetic?', a: 'The Twins are built from real people. Every Twin is grounded in survey data collected from an actual human participant — their attitudes, habits, purchase drivers, and behavioral patterns. The Twin responds as that person would, based on what they told us — it is not a made-up persona.' },
      { q: 'How recent is the data?', a: 'The study was conducted in 2026, making it one of the most current large-scale qualitative datasets for the breakfast category. The Twins reflect consumer attitudes and behaviors as they stand today — including the rise of high-protein and clean-label preferences and evolving plant-based milk adoption.' },
    ],
  },
  {
    group: 'About the Twins',
    items: [
      { q: 'How accurate are the AI Consumer Twins?', a: 'Each Twin is built directly from that participant’s survey responses. 10% of Twins are manually reviewed by a researcher against the source data before going live to validate behavioral fidelity. That said, Twins are behavioral models, not perfect replicas.' },
      { q: 'Can a Twin give a wrong answer?', a: 'A Twin can give an answer that doesn’t perfectly reflect what that individual would say. Every response is grounded in their source survey data with confidence guardrails in place. For decisions that require high certainty, treat Twin responses as a directional signal.' },
      { q: 'What’s the difference between a Twin and a survey?', a: 'A traditional survey gives you one response per question per participant — a snapshot. A Twin gives you an ongoing, queryable behavioral model: you can ask follow-up questions, probe motivations, test concepts, and explore reactions interactively.' },
    ],
  },
  {
    group: 'About the free trial',
    items: [
      { q: 'What exactly is included in the free study?', a: 'Your free account includes one complete use case — your choice of Chat (up to 3 queries), Qual/Quant Survey, Concept Test, or Packaging Test — run against one Segment of your choosing. You pick a Breakfast Category and a Segment within it, and your study runs against the AI Consumer Twins in that Segment.' },
      { q: 'Can I run the same use case twice?', a: 'The free trial covers one use case run. To run additional studies — the same use case against a different segment, or a different use case — you can purchase additional studies at $5 per Twin via Stripe, or talk to us about full access.' },
      { q: 'How long does it take to get results?', a: 'Results are typically delivered within a few hours of your study completing, with an email link to your full report. For Chat sessions, responses are available immediately during the session.' },
    ],
  },
  {
    group: 'About access and pricing',
    items: [
      { q: 'What happens after the free study?', a: 'You have two options: run additional studies pay-as-you-go via Stripe (any use case, any segment, no commitment), or talk to the BluePill team about full access.' },
      { q: 'What does full access include?', a: 'Full access is a commercial engagement with the BluePill team: unlimited studies across every Twin and all segments, tailored workflows across concepts, packaging, claims, and messaging, custom Twin studies in your own category, and dedicated support.' },
      { q: 'Is my data kept private?', a: 'Yes. All participant data is anonymized — no personally identifiable information is surfaced in the marketplace or in any study results. Your account data is used only to personalize your experience and is never shared with third parties. All data is handled in compliance with CCPA and GDPR.' },
    ],
  },
];

const FaqItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 py-3.5 text-left">
        <span className="text-[14px] font-medium text-[#111]">{q}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-[#6b6b6b] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-4 text-[13px] leading-relaxed text-[#6b6b6b]">{a}</p>}
    </div>
  );
};

/** Public marketing landing — ported from breakfast_landing_page.html (teal). */
const BreakfastLanding: React.FC = () => {
  const navigate = useNavigate();
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [useCaseCount, setUseCaseCount] = useState(4);

  useEffect(() => {
    getSubCategories().then(setSubs).catch(() => { /* hero falls back to defaults */ });
    getUseCases().then((u) => setUseCaseCount(u.length)).catch(() => { /* keep 4 */ });
  }, []);

  const getFreeAccess = () => navigate('/marketplace/run');
  const goToLogin = () => navigate('/marketplace/run', { state: { mode: 'login' } });

  return (
    <div className="min-h-screen bg-white text-[#111]">
      {/* Nav */}
      <header className="sticky top-0 z-10 border-b border-black/10 bg-white">
        <div className="flex items-center justify-between px-8 py-3">
          <div className="flex items-center gap-3">
            <a href="https://blue-pill.ai/" target="_blank" rel="noopener noreferrer" className="flex items-center overflow-hidden hover:opacity-80" aria-label="BluePill">
              <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="-my-4 h-20 w-auto object-contain" />
            </a>
            <span className="hidden h-4 w-px bg-black/15 sm:block" />
            <span className="hidden items-center gap-1.5 rounded-full bg-[#FEF3C7] px-3 py-1 text-[11px] font-medium text-[#92600A] sm:inline-flex">
              <Award className="h-3 w-3" /> Gartner 2026 · Polished Diamond
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MktButton variant="secondary" onClick={goToLogin}>Log in</MktButton>
            <MktButton onClick={getFreeAccess}>Get free access</MktButton>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-[960px] px-8 pb-12 pt-14 text-center">
        <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8FCFA] px-3 py-1 text-[11px] font-medium text-[#0B7C74]">
            <Globe className="h-3 w-3" /> World&apos;s first
          </span>
          <span className="inline-flex items-center rounded-full bg-[#f5f5f3] px-3 py-1 text-[11px] font-medium text-[#6b6b6b]">
            {PARTICIPANT_LABEL} real humans · {subs.length || 6} breakfast categories
          </span>
        </div>
        <h1 className="mx-auto max-w-2xl text-[30px] font-medium leading-tight tracking-tight text-[#111]">
          {PARTICIPANT_LABEL} real breakfast consumers. Available to talk, right now.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-[14px] leading-relaxed text-[#6b6b6b]">
          Surveyed, modelled, and available to talk to. Run concept tests, packaging studies, and qual research against the
          people who actually buy these products — in minutes, not months.
        </p>
        <div className="mt-6"><MktButton onClick={getFreeAccess}>Get free access →</MktButton></div>

        <div className="mx-auto mt-10 grid max-w-md grid-cols-3 gap-3">
          {[
            { v: PARTICIPANT_LABEL, l: 'AI Consumer Twins' },
            { v: subs.length || 6, l: 'Breakfast categories' },
            { v: useCaseCount, l: 'Use cases' },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-[#f5f5f3] px-4 py-3.5 text-center">
              <div className="text-[22px] font-medium text-[#111]">{s.v}</div>
              <div className="mt-0.5 text-[12px] text-[#6b6b6b]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Explore */}
      <section className="border-t border-black/10">
        <div className="mx-auto max-w-[960px] px-8 py-12">
          <div className="mb-5 flex items-baseline justify-between gap-4">
            <div>
              <Eyebrow>Explore the marketplace</Eyebrow>
              <h2 className="mt-1.5 text-[18px] font-medium text-[#111]">Browse Segments by Breakfast Category</h2>
            </div>
            <span className="hidden text-[12px] text-[#6b6b6b] sm:block">Click a Segment to preview</span>
          </div>
          <TwinExplorer subs={subs} ctaLabel="Sign up to query this Twin →" onCta={getFreeAccess} locked />
        </div>
      </section>

      {/* What you can do */}
      <section className="border-t border-black/10">
        <div className="mx-auto max-w-[960px] px-8 py-12">
          <Eyebrow>What you can do</Eyebrow>
          <h2 className="mb-5 mt-1.5 text-[18px] font-medium text-[#111]">Four ways to talk to your consumers</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASE_ORDER.map((key) => {
              const m = USE_CASE_META[key];
              return (
                <button
                  key={key}
                  onClick={() => navigate('/marketplace/run', { state: { useCase: key } })}
                  className="flex flex-col gap-3 rounded-xl border border-black/10 bg-white p-5 text-left transition-colors hover:border-black/20"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: m.tileBg }}>{m.icon}</div>
                  <div>
                    <div className="text-[15px] font-medium text-[#111]">{m.title}</div>
                    <p className="mt-1 text-[13px] leading-normal text-[#6b6b6b]">{m.desc}</p>
                  </div>
                  <div className="mt-auto pt-1 text-[11px]" style={{ color: m.limitColor }}>{m.limit}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex flex-col items-center gap-2">
            <MktButton onClick={getFreeAccess}>Get free access →</MktButton>
            <span className="text-[11px] text-[#999]">First use case free · No credit card required</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-black/10">
        <div className="mx-auto max-w-[760px] px-8 py-12">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mb-6 mt-1.5 text-[18px] font-medium text-[#111]">Common questions</h2>
          <div className="space-y-8">
            {FAQ_GROUPS.map((g) => (
              <div key={g.group}>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.05em] text-[#6b6b6b]">{g.group}</div>
                <div className="rounded-xl border border-black/10 bg-white px-5">
                  {g.items.map((it) => <FaqItem key={it.q} q={it.q} a={it.a} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-t border-black/10 bg-[#f5f5f3]">
        <div className="mx-auto max-w-[760px] px-8 py-14 text-center">
          <h2 className="text-[20px] font-medium text-[#111]">Ready to talk to your consumers?</h2>
          <p className="mx-auto mt-2.5 max-w-sm text-[13px] leading-relaxed text-[#6b6b6b]">
            Create a free account. Run your first use case against any audience Segment. No credit card required.
          </p>
          <div className="mt-6"><MktButton onClick={getFreeAccess}>Get free access →</MktButton></div>
        </div>
      </section>
    </div>
  );
};

export default BreakfastLanding;
